import { EditableTitle } from "@/components/EditableText";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { ArtifactMetadata } from "@/components/display/attributes/ArtifactMetadata";
import {
  SkillCategory,
  SkillCategorySelect,
  SkillContentFields,
} from "@/components/skill";
import { useSkillData } from "@/data/use-skill-data";
import { useSkillsData } from "@/data/use-skills-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { toast } from "sonner";

export default function SkillCreatePage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { skills } = useSkillsData(groupUuid, kitUuid);
  const skillUuid = skills[0]?.uuid;
  const { updateSkill, skill } = useSkillData(groupUuid, kitUuid, skillUuid);

  const handleCategoryChange = async (category: SkillCategory | null) => {
    try {
      await updateSkill({ category });
      toast.success("Category updated successfully!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while updating the category.");
    }
  };

  const handleTitleChange = async (name: string) => {
    updateSkill({ name });
  };

  if (!skill) {
    return null;
  }

  return (
    <Page document>
      <PageHeader document>
        <PageHeader.Title>
          <EditableTitle value={skill.name} onUpdate={handleTitleChange} />
        </PageHeader.Title>
      </PageHeader>
      <PageBody document>
        <PageBody.Metadata className="justify-between">
          <PageBody.MetadataGroup>
            <SkillCategorySelect
              value={skill.category as SkillCategory | null}
              onValueChange={handleCategoryChange}
              includeNoneOption={true}
              includeLabel={false}
              className="grid gap-2"
            />
          </PageBody.MetadataGroup>
          <PageBody.MetadataGroup>
            <ArtifactMetadata artifact={skill} />
          </PageBody.MetadataGroup>
        </PageBody.Metadata>
        <SkillBody skillUuid={skill.uuid} />
      </PageBody>
    </Page>
  );
}

function SkillBody({ skillUuid }: { skillUuid: string }) {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { updateSkill, skill } = useSkillData(groupUuid, kitUuid, skillUuid);

  const handleMetadataDescriptionUpdate = (metadataDescription: string) => {
    if (skill) {
      updateSkill({ metadataDescription });
    }
  };

  const handleContentUpdate = (html: string) => {
    if (skill) {
      updateSkill({ content: html });
    }
  };

  if (!skill || !groupUuid || !kitUuid) {
    return null;
  }

  return (
    <div className="flex w-full flex-grow flex-col gap-4 overflow-auto">
      <SkillContentFields
        skillUuid={skill.uuid}
        groupUuid={groupUuid}
        kitUuid={kitUuid}
        skillName={skill.name}
        metadataDescription={skill.metadataDescription}
        content={skill.content}
        dependencies={skill.dependencies}
        onMetadataDescriptionUpdate={handleMetadataDescriptionUpdate}
        onContentUpdate={handleContentUpdate}
      />
    </div>
  );
}
